import { Dropdown, IDropdownOption, MessageBar, MessageBarType, PrimaryButton, Spinner, SpinnerSize, Stack, TextField } from '@fluentui/react';
import { createPatientInsurance } from '../../../api/patients';
import React, { useMemo, useState } from 'react';
import { Payer } from '../../../redux/visits/types';

interface Props {
  patientId: string;
  payers: Payer[];
  onSuccess: () => void;
}

export const CreatePatientInsurancePlanForm = ({ patientId, payers, onSuccess }: Props) => {
  const [payerId, setPayerId] = useState<string>();
  const [planId, setPlanId] = useState<string>();
  const [memberId, setMemberId] = useState<string>();
  const [groupId, setGroupId] = useState<string>();
  const [isLoading, setLoading] = useState(false);
  const [error, setError] = useState<string>();

  const showGroupIdField: boolean = useMemo(() => {
    const selectedPayer = payers.find((payer) => payer.id === payerId);
    return !!selectedPayer?.requestGroupId;
  }, [payerId, payers]);

  const payerNameList: IDropdownOption[] = useMemo(() => payers.map((payer) => ({ key: payer.id, text: payer.shortName })), [payers]);
  const planNameList: IDropdownOption[] = useMemo(() => {
    const payer = payers.find((payer) => payer.id === payerId);
    const plans = payer?.athenaPlans ?? [];
    return plans.map((plan) => ({ key: plan.packageId, text: `${plan.name} - [${plan.packageId}]` }));
  }, [payers, payerId]);

  const canSubmit = !!payerId && !!memberId && (showGroupIdField ? !!groupId : true) && (planNameList.length ? !!planId : true);

  function onClickedSave() {
    if (!payerId || !memberId) {
      return;
    }
    setError(undefined);
    setLoading(true);
    createPatientInsurance(patientId, payerId, memberId, groupId, planId)
      .then(() => onSuccess())
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }

  return (
    <Stack tokens={{ childrenGap: 16 }}>
      <div style={{ marginTop: 20 }}>
        {isLoading && <Spinner size={SpinnerSize.large} style={{ padding: 20 }} label="Saving patient insurance information..." ariaLive="assertive" />}
        {error && <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>}
      </div>
      <Dropdown
        label="Payer"
        placeholder="Select a payer"
        onChange={(_ev, option) => {
          if (option?.key) {
            setPayerId(`${option.key}`);
          }
          setPlanId(undefined);
        }}
        options={payerNameList}
        data-tid="dd_payers"
        disabled={isLoading}
        required
      />
      {planNameList.length && (
        <Dropdown
          label="Plan Name"
          placeholder="Select a plan"
          onChange={(_ev, option) => option?.key && setPlanId(`${option.key}`)}
          options={planNameList}
          disabled={!payerId}
          required
        />
      )}
      <TextField label="Member ID" onChange={(ev, newValue) => setMemberId(newValue)} data-tid="inp_memberId" disabled={isLoading} required />
      {showGroupIdField && <TextField label="Group ID" onChange={(ev, newValue) => setGroupId(newValue)} data-tid="inp_groupId" required />}
      <PrimaryButton onClick={onClickedSave} style={{ marginTop: 20 }} text="Save Insurance Plan" disabled={!canSubmit || isLoading} data-tid="btn_save" />
    </Stack>
  );
};
